import {
  useQuery,
} from '@tanstack/react-query';
import {
  MultiMediaResponse,
} from 'dtos';
import {
  getFileExtension,
} from '@populateui/utils';
import {
  getTextFileContent,
} from 'pages/Dashboard/services/api';
import {
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  getUrlFromXML,
  mimeTypesByMultimediaType,
  thumbnailPreviewableExtensions,
  previewableExtensions,
  extensionToPreviewTag,
  PreviewableExtension,
} from 'utils/files';
import {
  MediaOriginatorType,
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  useGetMediaSettings,
} from 'pages/Dashboard/hooks/useUploadPatientMedia';
import Image from 'pages/Dashboard/components/Image';
import {
  useMedia,
} from 'core/hooks';

const XML_STYLESHEET_PATH = '/assets/CDA.xsl';

function useGetFileContent(fileUrl: string, extension: string) {
  return useQuery({
    queryKey: ['fileSource', fileUrl, extension],
    queryFn: () => Promise.all([
      getTextFileContent(fileUrl, extension === 'xml' || extension === 'txt'),
      getTextFileContent(XML_STYLESHEET_PATH, extension === 'xml'),
    ]),
    enabled: !isNil(fileUrl) && !isNil(extension),
  });
}

export function useGetFileSource(fileUrl: string, extension: string) {
  const [src, setSrc] = useState<string | null>(null);
  const {
    data,
    isFetching,
  } = useGetFileContent(fileUrl, extension);

  const [textContent, stylesheet] = data ?? [];

  useEffect(() => {
    if (!isFetching && extension === 'xml' && !isNil(textContent) && !isNil(stylesheet)) {
      const url = getUrlFromXML(textContent, stylesheet);
      setSrc(url);
    }
  }, [isFetching, extension, textContent, stylesheet]);

  return {
    src,
    textContent,
    isFetching,
  };
}

type Props = {
  media: MultiMediaResponse;
  thumbnail?: boolean;
};

export function useGetMediaPreviewInfo({
  media,
  thumbnail = false,
}: Props) {
  const fileUrl = media?.url?.value ?? '';
  const extension = getFileExtension(media?.fileName ?? '') ?? '';
  const {
    src,
    textContent,
    isFetching,
  } = useGetFileSource(fileUrl, extension);

  const previewAvailable = (thumbnail
    ? thumbnailPreviewableExtensions
    : previewableExtensions
  ).includes(extension);

  const srcUrl = previewAvailable && extension !== 'txt'
    ? `${extension === 'xml' ? src : fileUrl}#toolbar=${Number(!thumbnail)}`
    : undefined;

  const PreviewTag = isNil(srcUrl)
    ? Image
    : extensionToPreviewTag[extension as PreviewableExtension] ?? Image;

  return {
    src: srcUrl,
    previewAvailable,
    previewImage: /png|jpg|jpeg/i.test(extension),
    previewPdf: /pdf/i.test(extension),
    renderAsText: extension === 'txt',
    content: extension === 'txt' ? textContent : undefined,
    isFetching,
    PreviewTag,
  };
}

export function useGetAllowedFileTypes(multimediaType?: MultimediaType): string {
  const { allowedExtensions = [] } = useGetMediaSettings();

  const extensions = mimeTypesByMultimediaType[multimediaType ?? ''] ?? allowedExtensions;
  return extensions
    .map((extension: string) => `.${extension}`)
    .join(', ');
}

export function useProfilePicture(
  id?: number,
  originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
  shouldRequest = true,
) {
  const { requestMedia, getMediaPathById, getMediaById, isFetching } = useMedia();

  useEffect(() => {
    if (shouldRequest && !isNil(id) && id > 0) {
      requestMedia([id], originatorType);
    }
  }, [id, originatorType, requestMedia, shouldRequest]);

  return {
    src: getMediaPathById(id ?? 0, originatorType) ?? undefined,
    media: getMediaById(id ?? 0, originatorType),
    isFetching: isFetching(originatorType, id),
  };
}

export function useUserProfilePicture(userId?: number, shouldRequest = true) {
  return useProfilePicture(userId, MediaOriginatorType.User, shouldRequest);
}

export function useUserProfilePicturePath(userId?: number) {
  const { getMediaPathById } = useMedia();
  return getMediaPathById(userId ?? 0, MediaOriginatorType.User) ?? undefined;
}
